import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { 
  Copy, 
  Save, 
  X,
  AlertCircle,
  Info,
  Plus
} from "lucide-react";
import { toast } from "sonner";

const EntityCloneModal = ({ entity, open, onOpenChange, onClone, sourceTargetPairs }) => {
  const [clonedEntity, setClonedEntity] = useState({});
  const [selectedSourceTarget, setSelectedSourceTarget] = useState("");

  React.useEffect(() => {
    if (entity) {
      // Create a clone with new ID and cleared specific fields
      const clone = {
        ...entity,
        id: null, // Will be assigned by backend
        source: '',
        target: '',
        migrationObject: null,
        mainTarget: null,
        // Keep configuration but allow modification
        dependentTargets: entity.dependentTargets ? [...entity.dependentTargets] : [],
        mappingTargets: entity.mappingTargets ? [...entity.mappingTargets] : [],
        incompatible: entity.incompatible ? [...entity.incompatible] : []
      };
      setClonedEntity(clone);
    }
  }, [entity]);

  if (!entity) return null;

  const handleFieldChange = (field, value) => {
    setClonedEntity(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSourceTargetChange = (value) => {
    const [source, target] = value.split('-');
    if (source && target) {
      setClonedEntity(prev => ({
        ...prev,
        source,
        target
      }));
      setSelectedSourceTarget(value);
    }
  };

  const handleClone = () => {
    if (!clonedEntity.source || !clonedEntity.target) {
      toast.error("Please select source and target systems");
      return;
    }

    if (onClone) {
      onClone(clonedEntity);
    }
    toast.success("Entity cloned successfully!");
    onOpenChange(false);
  };

  const handleCancel = () => {
    setClonedEntity({});
    setSelectedSourceTarget("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-white" style={{backgroundColor: 'white', color: '#0f172a'}}>
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2" style={{color: '#0f172a'}}>
            <Copy className="w-5 h-5" />
            <span>Clone Entity: {entity.source} → {entity.target}</span>
          </DialogTitle>
          <DialogDescription style={{color: '#64748b'}}>
            Create a new entity based on the current configuration
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Source and Target Selection */}
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="text-sm flex items-center space-x-2">
                <Plus className="w-4 h-4" />
                <span>New Entity Configuration</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Quick Select Source-Target Pair</Label>
                  <Select value={selectedSourceTarget} onValueChange={handleSourceTargetChange}>
                    <SelectTrigger className="bg-white" style={{backgroundColor: 'white', color: '#0f172a'}}>
                      <SelectValue placeholder="Choose existing pair..." />
                    </SelectTrigger>
                    <SelectContent style={{backgroundColor: 'white', color: '#0f172a'}}>
                      {sourceTargetPairs?.map((pair) => (
                        <SelectItem key={`${pair.source}-${pair.target}`} value={`${pair.source}-${pair.target}`}>
                          {pair.source} → {pair.target}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cloneSource" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Source System</Label>
                  <Input
                    id="cloneSource"
                    value={clonedEntity.source || ''}
                    onChange={(e) => handleFieldChange('source', e.target.value)}
                    placeholder="e.g., zendesk, jira, servicenow"
                    className="bg-white border-slate-300 text-slate-900"
                    style={{backgroundColor: 'white', color: '#0f172a'}}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cloneTarget" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Target System</Label>
                  <Input
                    id="cloneTarget"
                    value={clonedEntity.target || ''}
                    onChange={(e) => handleFieldChange('target', e.target.value)}
                    placeholder="e.g., freshservice, slack"
                    className="bg-white border-slate-300 text-slate-900"
                    style={{backgroundColor: 'white', color: '#0f172a'}}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Basic Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cloneMigrationObject" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Migration Object ID</Label>
                  <Input
                    id="cloneMigrationObject"
                    type="number"
                    value={clonedEntity.migrationObject || ''}
                    onChange={(e) => handleFieldChange('migrationObject', parseInt(e.target.value) || null)}
                    placeholder="Migration object reference"
                    className="bg-white border-slate-300 text-slate-900"
                    style={{backgroundColor: 'white', color: '#0f172a'}}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cloneMainTarget" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Main Target ID</Label>
                  <Input
                    id="cloneMainTarget"
                    type="number"
                    value={clonedEntity.mainTarget || ''}
                    onChange={(e) => handleFieldChange('mainTarget', parseInt(e.target.value) || null)}
                    placeholder="Main target reference"
                    className="bg-white border-slate-300 text-slate-900"
                    style={{backgroundColor: 'white', color: '#0f172a'}}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cloneCustomFieldKey" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Custom Field Key</Label>
                  <Input
                    id="cloneCustomFieldKey"
                    value={clonedEntity.customFieldKey || ''}
                    onChange={(e) => handleFieldChange('customFieldKey', e.target.value)}
                    placeholder="e.g., custom_fields"
                    className="bg-white border-slate-300 text-slate-900"
                    style={{backgroundColor: 'white', color: '#0f172a'}}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Advanced Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Configuration Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="cloneDelimiterEnabled"
                    checked={clonedEntity.isDelimiterEnabled || false}
                    onCheckedChange={(checked) => handleFieldChange('isDelimiterEnabled', checked)}
                  />
                  <Label htmlFor="cloneDelimiterEnabled" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Enable Delimiter</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="cloneMultipleEntities"
                    checked={clonedEntity.selectMultipleEntities || false}
                    onCheckedChange={(checked) => handleFieldChange('selectMultipleEntities', checked)}
                  />
                  <Label htmlFor="cloneMultipleEntities" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Allow Multiple Entities</Label>
                </div>
              </div>

              {clonedEntity.isDelimiterEnabled && (
                <div className="space-y-2">
                  <Label htmlFor="cloneDelimiter" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Delimiter Character</Label>
                  <Input
                    id="cloneDelimiter"
                    value={clonedEntity.delimiter || ''}
                    onChange={(e) => handleFieldChange('delimiter', e.target.value)}
                    placeholder="|"
                    maxLength={5}
                    className="bg-white border-slate-300 text-slate-900"
                    style={{backgroundColor: 'white', color: '#0f172a'}}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="cloneDependentTargets" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Dependent Targets (comma-separated IDs)</Label>
                <Input
                  id="cloneDependentTargets"
                  value={clonedEntity.dependentTargets ? clonedEntity.dependentTargets.join(', ') : ''}
                  onChange={(e) => handleFieldChange('dependentTargets', 
                    e.target.value.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n))
                  )}
                  placeholder="19, 133"
                  className="bg-white border-slate-300 text-slate-900"
                  style={{backgroundColor: 'white', color: '#0f172a'}}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cloneIncompatible" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Incompatible Items (comma-separated)</Label>
                <Textarea
                  id="cloneIncompatible"
                  value={clonedEntity.incompatible ? clonedEntity.incompatible.join(', ') : ''}
                  onChange={(e) => handleFieldChange('incompatible', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                  placeholder="Service Request, Changes, Problems, Articles"
                  rows={3}
                  className="bg-white border-slate-300 text-slate-900"
                  style={{backgroundColor: 'white', color: '#0f172a'}}
                />
              </div>
            </CardContent>
          </Card>

          {/* Information Card */}
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Info className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-800">Cloning Information</span>
              </div>
              <p className="text-sm text-green-700 mt-2">
                This will create a new entity with similar configuration but different source/target systems. 
                Complex JSON configurations (sourceField, targetField, sourceExtender) will be copied as-is 
                and may need manual adjustment for the new systems.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-4 border-t">
          <div>
            <span className="text-sm text-blue-600 flex items-center space-x-1">
              <Copy className="w-3 h-3" />
              <span>Creating new entity based on: {entity.source} → {entity.target}</span>
            </span>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={handleCancel}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleClone}>
              <Save className="w-4 h-4 mr-2" />
              Clone Entity
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EntityCloneModal;